import { lazy, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { useAuth } from './state/auth'
import { LoginPage } from './pages/LoginPage'
import { PasswordResetPage } from './pages/PasswordResetPage'
import { LandingPage } from './pages/LandingPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { AccessPendingPage } from './pages/AccessPendingPage'
import { BrandLogo } from './components/BrandLogo'

const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })))
const TransactionsPage = lazy(() => import('./pages/TransactionsPage').then((module) => ({ default: module.TransactionsPage })))
const AccountsPage = lazy(() => import('./pages/AccountsPage').then((module) => ({ default: module.AccountsPage })))
const AssetsPage = lazy(() => import('./pages/AssetsPage').then((module) => ({ default: module.AssetsPage })))
const BudgetsPage = lazy(() => import('./pages/BudgetsPage').then((module) => ({ default: module.BudgetsPage })))
const BillsPage = lazy(() => import('./pages/BillsPage').then((module) => ({ default: module.BillsPage })))
const SalaryPage = lazy(() => import('./pages/SalaryPage').then((module) => ({ default: module.SalaryPage })))
const FreelancePage = lazy(() => import('./pages/FreelancePage').then((module) => ({ default: module.FreelancePage })))
const ReportsPage = lazy(() => import('./pages/ReportsPage').then((module) => ({ default: module.ReportsPage })))
const AuditPage = lazy(() => import('./pages/AuditPage').then((module) => ({ default: module.AuditPage })))
const AdminPage = lazy(() => import('./pages/AdminPage').then((module) => ({ default: module.AdminPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((module) => ({ default: module.SettingsPage })))

function ProtectedApp() {
  const { access, configured, loading, user } = useAuth()
  if (loading) return <div className="app-loading"><BrandLogo /><p>Menyiapkan Dhuwit kamu…</p></div>
  if (!configured || !user) return <Navigate to="/login" replace />
  if (access && !access.allowed) return <AccessPendingPage />
  return <AppShell key={user.id} />
}

function StaffRoute({ children }: { children: ReactNode }) {
  const { access } = useAuth()
  return access?.role === 'admin' || access?.role === 'developer' ? children : <Navigate to="/app" replace />
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<PasswordResetPage />} />
      <Route path="/app" element={<ProtectedApp />}>
        <Route index element={<DashboardPage />} />
        <Route path="transactions" element={<TransactionsPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="budgets" element={<BudgetsPage />} />
        <Route path="bills" element={<BillsPage />} />
        <Route path="salary" element={<SalaryPage />} />
        <Route path="freelance" element={<FreelancePage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="admin" element={<StaffRoute><AdminPage /></StaffRoute>} />
        <Route path="audit" element={<StaffRoute><AuditPage /></StaffRoute>} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  )
}
